import type { ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { BackIcon, ChevronRightIcon } from '../../components/Icons'
import { useBreitesFenster } from '../../hooks/useBreitesFenster'
import { useTexte } from '../../texte'

/** Was jede Zeile der Leiste in jedem Fall trägt. */
interface Zeile {
  /** Steht in der Adresse. Kleingeschrieben, ohne Umlaute. */
  schluessel: string
  titel: string
  /**
   * Das Symbol vor dem Wort — **Pflicht und nicht optional**.
   *
   * Aus dem Betrieb: „vor jeder Einstellungskategorie sollte ein passendes
   * Symbol stehen." Wäre das Feld freiwillig, wäre es eine Einladung, die
   * nächste Kategorie ohne eines nachzuschieben — und dann steht eine Liste
   * da, in der eine Zeile aus der Reihe fällt.
   */
  symbol: ReactNode
  /**
   * Eine Zahl, die an der Zeile hängt — **nur wenn sie größer als null ist.**
   *
   * Aus dem Betrieb: „bei Anfragen: Zähler in der Leiste anzeigen, wieviele
   * Anfragen offen sind (in rot)." Sie steht rot da, weil sie eine offene
   * Entscheidung meint und keine Auskunft: eine Anfrage, die man übersieht,
   * ist für den Anfragenden nicht von einer Ablehnung zu unterscheiden.
   *
   * **Eine Null wird nicht angezeigt.** „Anfragen (0)" wäre eine Marke über
   * nichts — und rot obendrein, also ein Alarm ohne Anlass.
   */
  zaehler?: number
}

/**
 * Eine Kategorie: was in der Leiste steht und was rechts erscheint.
 *
 * `inhalt` ist eine Funktion und kein fertiges Element — sonst würde jede
 * Kategorie bei jedem Zeichnen aufgebaut, auch die sechs, die niemand ansieht.
 * Manche davon fragen den Server.
 *
 * **Und eine Zeile kann stattdessen woandershin führen** (`ziel`). Genau das
 * braucht „Administration": eine eigene Seite mit eigenen Kategorien, aber
 * erreichbar aus der Liste, in der man sie sucht. Als Vereinigungstyp, damit
 * der Übersetzer festhält, dass es genau eines von beiden ist — eine Zeile mit
 * beidem wäre eine Frage danach, was gewinnt, und eine ohne beides ein leeres
 * Blatt.
 */
type Zeigbar = Zeile & { inhalt: () => ReactNode; ziel?: never }

export type Kategorie = Zeigbar | (Zeile & { ziel: string; inhalt?: never })

/**
 * Einstellungen als Kategorien — eine Architektur, zwei Darstellungen (#39).
 *
 * Aus dem Issue: „Einstellungsseite ähnlich iOS Einstellungen (links
 * Kategorien in einer Fensterleiste, dort Auswahl, und dann die jeweiligen
 * Einstellungen im Fenster rechts)". Und nachgereicht aus dem Betrieb:
 * „überlege mal, ob wir das am Telefon auch machen: Kategorien auf einer
 * Seite, von dort Sprung zur Einstellung."
 *
 * **Das ist derselbe Aufbau, nicht zwei.** Ab 900 Punkten stehen Leiste und
 * Inhalt nebeneinander, darunter nacheinander — genau wie `.shell` es mit
 * Chatliste und Verlauf tut. Dieselbe Grenze wie dort, denn eine zweite
 * Umbruchbreite wäre eine zweite Wahrheit darüber, was ein großer Bildschirm
 * ist.
 *
 * **Die Wahl steht in der Adresse** (`?bereich=…`) und nicht bloß im Zustand:
 * sonst ist sie nach dem Neuladen weg und lässt sich niemandem schicken. Und
 * sie trägt zugleich den Unterschied der beiden Darstellungen, ohne dass es
 * zwei Zustände gäbe:
 *
 * |            | kein `bereich`      | `bereich` gesetzt          |
 * |------------|---------------------|----------------------------|
 * | unter 900  | die Liste als Seite | die Kategorie, mit Zurück  |
 * | ab 900     | die erste gewählt   | diese Kategorie            |
 *
 * Auf dem breiten Fenster ist also immer eine gewählt, auf dem schmalen nicht.
 * Das ist der ganze Unterschied — und er steht an dieser einen Stelle.
 *
 * **Eine unbekannte Angabe gilt wie keine.** Ein Verweis von gestern, dessen
 * Kategorie es nicht mehr gibt, führt auf die Liste (oder ab 900 auf die
 * erste) und nicht auf eine leere Seite.
 */
export function Kategorien({
  titel,
  zurueck,
  kategorien,
}: {
  /** Steht in der Kopfzeile, solange die Liste dasteht. */
  titel: string
  /** Der Zurück-Pfeil der Liste — wohin er führt, weiß die Seite. */
  zurueck: ReactNode
  kategorien: Kategorie[]
}) {
  const t = useTexte()
  const [suche, setSuche] = useSearchParams()

  const gewuenscht = suche.get('bereich')
  /*
   * **Nur Zeilen mit Inhalt kommen als gewählte in Frage.** Eine, die
   * woandershin führt, hat rechts nichts zu zeigen; `?bereich=` mit ihrem
   * Schlüssel ergäbe sonst eine leere Spalte — und dasselbe gilt für die
   * erste, die auf dem breiten Fenster ohne Angabe einspringt.
   */
  const zeigbare = kategorien.filter((k): k is Zeigbar => k.inhalt !== undefined)
  const gefunden = zeigbare.find((k) => k.schluessel === gewuenscht) ?? null

  /*
   * **Ab 900 ist immer eine gewählt.** Das steht hier und nicht im CSS, weil
   * der Inhalt sonst zwar sichtbar, aber leer wäre: die Spalte stünde da und
   * hätte nichts zu zeigen. Deshalb ist es die eine Frage, die der Browser
   * nicht allein beantworten kann — und deshalb wird zugehört und nicht
   * einmal gemessen (`useBreitesFenster`): sonst stimmte der Wert nach dem
   * Drehen des iPads nicht mehr.
   */
  const breit = useBreitesFenster()
  const aktiv = gefunden ?? (breit ? (zeigbare[0] ?? null) : null)

  const waehle = (schluessel: string) => {
    // `replace`, damit der Zurück-Pfeil des Browsers nicht durch jede
    // angesehene Kategorie zurückstolpert. Auf dem Telefon führt der eigene
    // Zurück-Pfeil zur Liste.
    const naechste = new URLSearchParams(suche)
    naechste.set('bereich', schluessel)
    setSuche(naechste, { replace: true })
  }

  const zurListe = () => {
    const naechste = new URLSearchParams(suche)
    naechste.delete('bereich')
    setSuche(naechste, { replace: true })
  }

  return (
    <div className="einstellungen" data-blatt={aktiv === null ? 'liste' : 'inhalt'}>
      <div className="einstellungen__leiste">
        <header className="bar">
          {zurueck}
          <h2 className="bar__title bar__grow">{titel}</h2>
        </header>
        <nav className="einstellungen__liste" aria-label={titel}>
          {kategorien.map((k) =>
            k.ziel !== undefined ? (
              /*
                **Diese Zeile verlässt die Seite**, statt rechts etwas zu
                zeigen -- und das Zeichen am Ende sagt es. Ohne es sähe sie aus
                wie eine Kategorie, die sich nur nicht markieren lässt.
              */
              <Link key={k.schluessel} className="einstellungen__eintrag" to={k.ziel}>
                <span className="einstellungen__symbol">{k.symbol}</span>
                <span className="einstellungen__titel">{k.titel}</span>
                <ChevronRightIcon size={18} className="einstellungen__weiter" />
              </Link>
            ) : (
              <button
                key={k.schluessel}
                type="button"
                className={
                  'einstellungen__eintrag'
                  + (aktiv?.schluessel === k.schluessel ? ' einstellungen__eintrag--aktiv' : '')
                }
                // **Der aktive Eintrag ist auch angesagt und nicht nur gefärbt.**
                // Eine Farbe allein erreicht niemanden, der vorlesen lässt.
                aria-current={aktiv?.schluessel === k.schluessel ? 'page' : undefined}
                /*
                  **Die Zahl steht auch im Namen des Knopfes.** Rot allein
                  erreicht niemanden, der vorlesen lässt -- und eine Ziffer
                  ohne Wort sagt nicht, wovon sie handelt. Ohne Zahl bleibt der
                  Name der Titel, sonst hinge an jeder Zeile ein „0 offen".
                */
                aria-label={
                  k.zaehler !== undefined && k.zaehler > 0
                    ? t.einstellungen.offen(k.titel, k.zaehler)
                    : undefined
                }
                onClick={() => waehle(k.schluessel)}
              >
                <span className="einstellungen__symbol">{k.symbol}</span>
                {/*
                  **Der Titel steht in einem eigenen Element und nicht nackt
                  daneben.**

                  Aus dem Betrieb: „wenn Willkommensnachricht ausgewählt ist:
                  fette Schrift -- passt nicht in die Leiste." Ein nackter
                  Textknoten in einem Flex-Kasten ist ein anonymes Element:
                  er lässt sich weder abschneiden noch mit
                  Auslassungspunkten enden lassen, er läuft einfach hinaus.
                  Mit dem `span` greift `.einstellungen__titel` -- und
                  seither endet ein zu langes Wort in der Leiste statt über
                  ihr.
                */}
                <span className="einstellungen__titel">{k.titel}</span>
                {k.zaehler !== undefined && k.zaehler > 0 && (
                  <span className="einstellungen__zaehler" aria-hidden="true">
                    {k.zaehler}
                  </span>
                )}
              </button>
            ),
          )}
        </nav>
      </div>

      <div className="einstellungen__inhalt">
        {aktiv !== null && (
          <>
            {/*
              Die Kopfzeile der Kategorie steht nur auf dem schmalen Fenster --
              daneben steht ja die Leiste mit demselben Wort. Sie wird per CSS
              ausgeblendet und nicht hier entfernt: sonst hinge das Aussehen an
              einer Messung, die beim Drehen des Geräts veraltet.
            */}
            <header className="bar einstellungen__kopf">
              <button
                type="button"
                className="icon-button"
                aria-label={t.einstellungen.zurueckZuDenKategorien}
                onClick={zurListe}
              >
                <BackIcon />
              </button>
              <h2 className="bar__title bar__grow">{aktiv.titel}</h2>
            </header>
            <div className="scroll-panel">{aktiv.inhalt()}</div>
          </>
        )}
      </div>
    </div>
  )
}
